import React, { useState, useEffect, useCallback } from 'react';
import Login from './Login';
import Register from './Register';
import Layout from './Layout';
import Dashboard from './Dashboard';
import Conversations from './Conversations';
import Orders from './Orders';
import Contacts from './Contacts';
import Flows from './Flows';
import Marketing from './Marketing';
import Campaigns from './Campaigns';
import Settings from './Settings';
import Admin from './Admin';
// ContingencyOrders.js se conserva como respaldo pero ya no se expone como UI (Fase 3).
import VideoAI from './VideoAI';
import Landing from './Landing';
import Legal, { LEGAL_PATHS } from './Legal';

// "/" es la landing pública; el panel vive en "/inicio" ("/dashboard" lo usa el backend en nginx).
const PATH_TO_SCREEN = {
  '/inicio': 'Dashboard',
  '/conversaciones': 'Conversaciones',
  '/pedidos': 'Pedidos',
  '/contactos': 'Contactos',
  '/flujos': 'Flujos',
  '/marketing': 'Marketing',
  '/campanas': 'Campañas',
  '/configuracion': 'Configuraciones',
  '/video-ia': 'Video IA',
};
const SCREEN_TO_PATH = {};
Object.keys(PATH_TO_SCREEN).forEach(k => { SCREEN_TO_PATH[PATH_TO_SCREEN[k]] = k; });

function readURL() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const screen = PATH_TO_SCREEN[path] || 'Dashboard';
  const phone = params.get('phone') || null;
  return { screen, phone };
}

function currentPath() {
  return window.location.pathname.replace(/\/+$/, '') || '/';
}

function hasSession() {
  try { return !!localStorage.getItem('talku_user'); } catch { return false; }
}

// Destino tras login: ?next= solo si es una ruta interna del mismo sitio.
function nextAfterLogin() {
  const next = new URLSearchParams(window.location.search).get('next');
  if (next && next.startsWith('/') && !next.startsWith('//')) return next;
  return '/inicio';
}

function AuthPage({ mode }) {
  const alreadyIn = hasSession();

  useEffect(() => {
    if (alreadyIn) window.location.replace('/inicio');
  }, [alreadyIn]);

  if (alreadyIn) return null;

  const handleLogin = (userData) => {
    localStorage.setItem('talku_user', JSON.stringify(userData));
    window.location.replace(nextAfterLogin());
  };

  if (mode === 'register') {
    return <Register onLogin={handleLogin} onShowLogin={() => window.location.assign('/login')} />;
  }
  return <Login onLogin={handleLogin} onShowRegister={() => window.location.assign('/registro')} />;
}

function MainApp() {
  const [user] = useState(() => {
    try {
      const saved = localStorage.getItem('talku_user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const [screen, setScreen] = useState(() => readURL().screen);
  const [convPhone, setConvPhone] = useState(() => readURL().phone);

  const pushURL = useCallback((s, phone) => {
    const base = SCREEN_TO_PATH[s] || '/inicio';
    const qs = (s === 'Conversaciones' && phone) ? `?phone=${encodeURIComponent(phone)}` : '';
    const target = base + qs;
    if (window.location.pathname + window.location.search !== target) {
      window.history.pushState({ screen: s, phone }, '', target);
    }
  }, []);

  useEffect(() => {
    const onPop = () => {
      // Volver atrás hacia la landing o el login: recargar para que App elija la página correcta.
      if (!PATH_TO_SCREEN[currentPath()]) { window.location.reload(); return; }
      const { screen: s, phone } = readURL();
      setScreen(s);
      if (s === 'Conversaciones' && phone) setConvPhone(phone);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Sin sesión en una ruta interna: al login, conservando a dónde iba.
  useEffect(() => {
    if (!user) {
      const target = window.location.pathname + window.location.search;
      window.location.replace('/login?next=' + encodeURIComponent(target));
    }
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem('talku_user');
    localStorage.removeItem('talku_token');
    window.location.replace('/login');
  };

  const handleNav = (s, phone) => {
    if (s === 'Conversaciones' && phone) setConvPhone(phone);
    else if (s !== 'Conversaciones') setConvPhone(null);
    setScreen(s);
    pushURL(s, phone);
  };

  if (!user) return null;

  return (
    <Layout
      user={user}
      activeNav={screen}
      onNav={handleNav}
      onLogout={handleLogout}
    >
      {screen === 'Dashboard'       && <Dashboard          user={user} onNav={handleNav} onLogout={handleLogout} />}
      {screen === 'Conversaciones'  && <Conversations      user={user} onNav={handleNav} onLogout={handleLogout} initialPhone={convPhone} noShell />}
      {screen === 'Pedidos'         && <Orders             user={user} onNav={handleNav} onLogout={handleLogout} noShell />}
      {screen === 'Contactos'       && <Contacts           user={user} onNav={setScreen} onLogout={handleLogout} noShell />}
      {screen === 'Flujos'          && <Flows              user={user} onNav={setScreen} onLogout={handleLogout} noShell />}
      {screen === 'Marketing'       && <Marketing          user={user} onNav={setScreen} onLogout={handleLogout} noShell />}
      {screen === 'Campañas'        && <Campaigns          user={user} onNav={setScreen} onLogout={handleLogout} noShell />}
      {screen === 'Configuraciones' && <Settings           user={user} onNav={setScreen} onLogout={handleLogout} noShell />}
      {screen === 'Video IA'        && <VideoAI            user={user} onNav={handleNav} onLogout={handleLogout} />}
    </Layout>
  );
}

function App() {
  const path = currentPath();
  if (path === '/admin') return <Admin />;
  if (path === '/') return <Landing />;
  if (path === '/login') return <AuthPage mode="login" />;
  if (path === '/registro') return <AuthPage mode="register" />;
  if (LEGAL_PATHS.includes(path)) return <Legal page={path.slice(1)} />;
  return <MainApp />;
}

export default App;
